import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Flex, Typography } from '@iheartjane/reefer';
import { useEffect, useState } from 'react';

import { ExpandableNav } from '@jane/business-admin/components/premium/ExpandableNav';
import { ExpandableNavItem } from '@jane/business-admin/components/premium/ExpandableNavItem';
import { MenuSelector } from '@jane/business-admin/components/premium/MenuSelector';
import { NavItem } from '@jane/business-admin/components/premium/NavItem';
import {
  saveCurrentBloomMenuId,
  useFetchCurrentBloomMenu,
} from '@jane/business-admin/data-access';

import styles from './premium.module.css';

export const Premium = () => {
  const hasCloudflareAccess = useFlag(FLAGS.c2PremiumCloudflareIntegrations);
  const hasFooterCustomizationAccess = useFlag(FLAGS.obCustomizePremiumFooter);
  const hasFooterImportAccess = useFlag(FLAGS.obImportPremiumFooter);
  const hasCustomComponentsAccess = useFlag(FLAGS.c2CustomComponents);

  const currentMenuQuery = useFetchCurrentBloomMenu();
  const [currentMenuId, setCurrentMenuId] = useState<string | undefined>(
    currentMenuQuery.data?.id.toString()
  );
  const showCloudflareNav =
    hasCloudflareAccess &&
    currentMenuQuery.data?.integration_method === 'cloudflare';

  const onMenuChange = (newMenuId: string) => {
    setCurrentMenuId(newMenuId);
    saveCurrentBloomMenuId(newMenuId);
  };

  useEffect(() => {
    if (currentMenuQuery.data) {
      setCurrentMenuId(currentMenuQuery.data.id.toString());
    }
  }, [currentMenuQuery.data]);

  return (
    <Flex flexDirection="column">
      <Flex
        className={styles['content']}
        mt={96}
        mx="auto"
        width="50%"
        flexDirection="column"
      >
        <Typography variant="h2-semibold" as="h1" mt={40} ml={40}>
          Welcome to Ecommerce Premium
        </Typography>
        <MenuSelector currentMenuId={currentMenuId} onChange={onMenuChange} />
        <NavItem title="My stores" to={`${currentMenuId}/stores`} />
        <ExpandableNav title="Global style settings">
          {hasCustomComponentsAccess ? (
            <ExpandableNavItem
              title="Menu editor"
              to={`${currentMenuId}/menu-editor`}
              position="single"
            />
          ) : (
            <>
              <ExpandableNavItem
                title="Fonts"
                to={`${currentMenuId}/fonts`}
                position="first"
              />
              <ExpandableNavItem
                title="Colors"
                to={`${currentMenuId}/colors`}
                position="last"
              />
            </>
          )}
        </ExpandableNav>
        <ExpandableNav title="Global shop settings">
          {!hasCustomComponentsAccess && (
            <ExpandableNavItem
              title="Logo"
              to={`${currentMenuId}/logo`}
              position="first"
            />
          )}
          <ExpandableNavItem
            title="Age gate"
            to={`${currentMenuId}/age-gate`}
            position={hasCustomComponentsAccess ? 'first' : undefined}
          />
          <ExpandableNavItem
            title="Main menu"
            to={`${currentMenuId}/main-menu`}
          />
          <ExpandableNavItem
            title="Sign-in prompt"
            to={`${currentMenuId}/sign-in-prompt`}
          />
          {hasFooterImportAccess && (
            <ExpandableNavItem title="Footer" to={`${currentMenuId}/footer`} />
          )}
          {hasFooterCustomizationAccess && (
            <ExpandableNavItem
              title="Footer template"
              to={`${currentMenuId}/footer-template`}
            />
          )}
          <ExpandableNavItem
            title="SEO customization"
            to={`${currentMenuId}/seo`}
          />
          <ExpandableNavItem
            title="External scripts"
            to={`${currentMenuId}/external-scripts`}
          />
          <ExpandableNavItem
            title="Google add-ons"
            to={`${currentMenuId}/google`}
            position={showCloudflareNav ? undefined : 'last'}
          />
          {showCloudflareNav && (
            <ExpandableNavItem
              title="Cloudflare Integrations"
              to={`${currentMenuId}/cloudflare`}
              position="last"
            />
          )}
        </ExpandableNav>
      </Flex>
    </Flex>
  );
};
